import { addHours } from "date-fns";
import { Trash2 } from "lucide-react";
import {
  Form,
  required,
  useCreate,
  useDelete,
  useGetIdentity,
  useInput,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";

import { DateTimeInput } from "@/components/admin/date-time-input";
import { SaveButton } from "@/components/admin/form";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import type { PersonalEvent } from "../types";
import {
  DEFAULT_EVENT_COLOR,
  EVENT_COLORS,
  getEventColorVar,
} from "./eventColors";

const RESOURCE = "personal_events";

export interface EventDialogProps {
  open: boolean;
  onClose: () => void;
  /** Event to edit; when absent the dialog creates a new event. */
  event?: PersonalEvent | null;
  /** Start of a new event (the clicked time slot). */
  defaultStart?: Date | null;
}

const validateEndAfterStart = (value: string, allValues: FieldValues) =>
  value && allValues.start_at && new Date(value) <= new Date(allValues.start_at)
    ? "crm.personal.events.invalid_range"
    : undefined;

const EventColorInput = () => {
  const translate = useTranslate();
  const { field } = useInput({ source: "color" });
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">
        {translate("crm.personal.events.fields.color")}
      </span>
      <div className="flex gap-2" role="radiogroup">
        {EVENT_COLORS.map((color) => (
          <button
            key={color}
            type="button"
            role="radio"
            aria-checked={field.value === color}
            aria-label={color}
            onClick={() => field.onChange(color)}
            className={cn(
              "size-7 rounded-full border-2 border-transparent transition-transform hover:scale-110",
              field.value === color && "border-foreground",
            )}
            style={{ backgroundColor: getEventColorVar(color) }}
          />
        ))}
      </div>
    </div>
  );
};

export const EventDialog = ({
  open,
  onClose,
  event,
  defaultStart,
}: EventDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const isEdit = !!event;

  const record = useMemo(() => {
    if (event) return event;
    const start = defaultStart ?? new Date();
    return {
      title: "",
      description: "",
      start_at: start.toISOString(),
      end_at: addHours(start, 1).toISOString(),
      color: DEFAULT_EVENT_COLOR,
    };
  }, [event, defaultStart]);

  const handleError = (error: unknown) =>
    notify(
      error instanceof Error ? error.message : "ra.notification.http_error",
      {
        type: "error",
      },
    );

  const handleSubmit = async (values: FieldValues) => {
    const data = {
      title: values.title,
      description: values.description || null,
      start_at: values.start_at,
      end_at: values.end_at,
      color: values.color ?? DEFAULT_EVENT_COLOR,
    };
    if (event) {
      await update(
        RESOURCE,
        { id: event.id, data, previousData: event },
        {
          onSuccess: () => {
            notify("crm.personal.events.updated");
            onClose();
          },
          onError: handleError,
        },
      );
      return;
    }
    await create(
      RESOURCE,
      { data: { ...data, sales_id: identity?.id } },
      {
        onSuccess: () => {
          notify("crm.personal.events.created");
          onClose();
        },
        onError: handleError,
      },
    );
  };

  const handleDelete = () => {
    if (!event) return;
    deleteOne(
      RESOURCE,
      { id: event.id, previousData: event },
      {
        onSuccess: () => {
          notify("crm.personal.events.deleted");
          onClose();
        },
        onError: handleError,
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="lg:max-w-lg overflow-y-auto max-h-9/10">
        {open && (
          <Form
            record={record}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>
                {translate(
                  isEdit
                    ? "crm.personal.events.edit"
                    : "crm.personal.events.create",
                )}
              </DialogTitle>
            </DialogHeader>
            <TextInput
              source="title"
              label="crm.personal.events.fields.title"
              validate={required()}
              autoFocus
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DateTimeInput
                source="start_at"
                label="crm.personal.events.fields.start_at"
                validate={required()}
              />
              <DateTimeInput
                source="end_at"
                label="crm.personal.events.fields.end_at"
                validate={[required(), validateEndAfterStart]}
              />
            </div>
            <TextInput
              source="description"
              label="crm.personal.events.fields.description"
              multiline
            />
            <EventColorInput />
            <DialogFooter className="w-full gap-2 sm:justify-between">
              {isEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  <Trash2 />
                  {translate("crm.personal.events.delete")}
                </Button>
              ) : (
                <span />
              )}
              <SaveButton />
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
